import { i18next } from '@vmos2/core';
import trim from 'lodash/trim';
import equals from 'validator/lib/equals';
import isAlpha from 'validator/lib/isAlpha';
import isAlphanumeric from 'validator/lib/isAlphanumeric';
import validateEmail from 'validator/lib/isEmail';
import length from 'validator/lib/isLength';
import isNumeric from 'validator/lib/isNumeric';

const StringType = {
  ALPHANUMERIC: 'alphanumeric',
  LETTERS: 'letters',
  NUMBERS: 'numbers',
} as const;

type StringTypeEnum = typeof StringType[keyof typeof StringType];

const validationType = {
  isRequired: 'isRequired',
  isEmail: 'isEmail',
  isPositiveInteger: 'isPositiveInteger',
  isEqual: 'isEqual',
};

const validateType = (value: string, type?: StringTypeEnum) => {
  if (type === StringType.LETTERS) {
    return isAlpha(value);
  }

  if (type === StringType.NUMBERS) {
    return isNumeric(value);
  }

  if (type === StringType.ALPHANUMERIC) {
    return isAlphanumeric(value);
  }

  return true;
};

const isPositiveInteger = (message: string) => (value: string) =>
  value && !isNumeric(value, { no_symbols: true }) && message;

const isEmail = (value: string) =>
  !value || validateEmail(trim(value)) ? undefined : i18next.t('validations:invalidEmail');

const isLength =
  (min = 6, max = undefined) =>
  (value = '') =>
    !length(value, { min, max }) && i18next.t('validations:isLength', { minCharacters: min });

const isExactLength = (number: number) => (value: string) =>
  !value || length(value, { min: number, max: number })
    ? undefined
    : i18next.t('validations:isLength', { minCharacters: number });

const isEqual = (valueA: string, valueB: string) => valueA && valueB && equals(valueA, valueB);

const isType = (type?: StringTypeEnum) => (value: string) =>
  !value || validateType(value, type) ? undefined : i18next.t('validations:invalidReferralCode');

const combineValidationRules = (validations: ((value: string, allValues: Record<string, unknown>) => boolean | string)[] | []) => (value: string, allValues: Record<string, unknown>) => {
  const errors = validations.map(validation => {
    const result = validation(value, allValues);

    return result || null;
  });

  return errors.filter(Boolean).length ? errors.join('\n') : undefined;
};

const isRequired =
  (message = i18next.t('validations:isRequired')) =>
  (value: string) =>
    !trim(value) && message;

export const validation = {
  [validationType.isEmail]: isEmail,
  [validationType.isEqual]: isEqual,
  [validationType.isPositiveInteger]: isPositiveInteger,
  [validationType.isRequired]: isRequired,
  combineValidationRules,
  isExactLength,
  isLength,
  isType,
  validationType,
};
